// Enables click-to-sort on a <table> element.
//
// Usage:
//   <table data-controller="table-sorter">
//     <thead><tr><th>Name</th><th>Count</th></tr></thead>
//     <tbody>...</tbody>
//   </table>
//
// Clicking a <th> sorts the tbody rows by that column. Clicking again reverses
// the sort direction. Numeric values (including currency and comma-formatted
// numbers like "$1,234.56") are sorted numerically; everything else sorts
// naturally ("Project 10" after "Project 2"). The active column gets a
// data-sort="asc"|"desc" attribute, which the table_sorter.css component uses
// to render ↑/↓ indicators.
import { Controller } from "@hotwired/stimulus";

// Exported for testing. Compares two cell text values: numerically if both
// parse as numbers (stripping currency symbols, commas, etc.), otherwise
// via natural locale-aware string comparison.
export function compareValues(a, b) {
  const toNum = (s) => {
    const match = String(s).match(/-?\d[\d,]*(?:\.\d+)?/);
    if (!match) return NaN;
    return parseFloat(match[0].replace(/,/g, ""));
  };
  const an = toNum(a);
  const bn = toNum(b);
  return !isNaN(an) && !isNaN(bn)
    ? an - bn
    : a.localeCompare(b, undefined, { numeric: true });
}

export default class extends Controller {
  connect() {
    this.sortCol = null;
    this.sortDir = null;
    this.clickHandlers = [];

    this.element.querySelectorAll("thead th").forEach((th, i) => {
      const handler = () => this.#sortBy(i, th);
      th.addEventListener("click", handler);
      this.clickHandlers.push({ element: th, handler });
    });
  }

  disconnect() {
    if (!this.clickHandlers) return;

    this.clickHandlers.forEach(({ element, handler }) => {
      element.removeEventListener("click", handler);
    });

    this.clickHandlers = [];
  }

  #sortBy(colIndex, th) {
    this.sortDir =
      this.sortCol === colIndex && this.sortDir === "asc" ? "desc" : "asc";
    this.sortCol = colIndex;

    this.element.querySelectorAll("thead th").forEach((h) => {
      h.removeAttribute("data-sort");
    });
    th.dataset.sort = this.sortDir;

    const tbody = this.element.tBodies[0];
    if (!tbody) return;

    const rows = Array.from(tbody.rows);
    const sortDir = this.sortDir;

    const performSort = () => {
      rows.sort((a, b) => {
        const av = a.cells[colIndex]?.textContent.trim() ?? "";
        const bv = b.cells[colIndex]?.textContent.trim() ?? "";
        return sortDir === "asc"
          ? compareValues(av, bv)
          : compareValues(bv, av);
      });
      rows.forEach((r) => tbody.appendChild(r));
      this.element.removeAttribute("data-sorting");
    };

    this.element.dataset.sorting = "true";

    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(performSort);
    } else {
      setTimeout(performSort, 0);
    }
  }
}
